/**
 * Initial click on the AddToBagButton should trigger the addToBagAction.graphql, subsequent clicks on &#x27;+&#x27; or &#x27;-&#x27; should trigger updateBagAction.graphql until the quantity reaches 1 and the user clicks on &#x27;-&#x27;, then it should trigger removeFromBagAction.graphql. When the quantity reaches maxQuantity, the &#x27;+&#x27; should be disabled
 *
 * @module views/components/AddToBagButton
 * @memberof -Common
 */
import './AddToBagButton.scss';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Button from '@ulta/core/components/Button/Button';
import Icon from '@ulta/core/components/Icon/Icon';
import Text from '@ulta/core/components/Text/Text';
import { useNextRender } from '@ulta/core/hooks/useNextRender/useNextRender';
import { useTimeout } from '@ulta/core/hooks/useTimeout/useTimeout';
import { useAriaLiveRegion } from '@ulta/core/providers/AriaLiveRegionProvider/AriaLiveRegionProvider';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import { patchDataLayer } from '@ulta/core/utils/datacapture/datacapture';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import * as utils from './AddToBagButton';

/**
 * Represents a AddToBagButton component
 *
 * @method
 * @param {AddToBagButtonProps} props - React properties passed from composition
 * @returns AddToBagButton
 */
export const AddToBagButton = function( props ){
  const [loader, setLoader] = useState( false );

  const [showAnimation, setShowAnimation] = useState( false );
  const [updateBagTriggered, setUpdateBagTriggered] = useState( false );
  const animationFlag = useRef( false );

  const lastAction = useRef();
  const mainButtonRef = useRef();
  const increaseButtonRef = useRef();
  const descreaseButtonRef = useRef();
  const shouldFocusIncrement = useRef( false );
  const wasOverlayOpen = useRef( false );
  const a11yAction = useRef( false );

  const {
    addToBagAction,
    commerceId,
    default: defaultState,
    fulfillmentType,
    inBagLabel,
    incrementBagAccessibilityLabel,
    invokeMutation,
    isSecondary,
    maxQuantity,
    productId,
    quantity,
    removeFromBagAction,
    skuId,
    successLabel,
    isSubscribed,
    subscriptionFrequency,
    updateBagAction,
    updateSuccessLabel,
    showUpdateBag,
    setIsFulfillmentChanged,
    isWebStickyATBButton,
    stickyAddToBagAccessibilityLabel
  } = props;

  const [scheduleFocus] = useNextRender();
  const { displayOverlay } = useOverlay();
  const { broadcastMessage } = useAriaLiveRegion();
  const { inflection } = useDeviceInflection();
  const [animationTimer] = useTimeout();

  /**
   * Handle add to bag response
   * -> We want to always run this any time quantity changes
   */
  const addToBagHandler = useCallback(
    utils.composeAddToBagResponse(
      { lastAction, animationFlag, quantity, descreaseButtonRef, increaseButtonRef, maxQuantity },
      { setLoader, setShowAnimation, setUpdateBagTriggered, scheduleFocus }
    ),
    [quantity]
  );

  useEffect( addToBagHandler, [addToBagHandler] );

  /**
   * Handle remove from bag resposne
   * -> We want to always run this any time quantity changes
   */
  const removeFromBagHandler = useCallback(
    utils.composeRemoveFromBagResponse(
      { lastAction, animationFlag, mainButtonRef, quantity },
      { setLoader, setShowAnimation, scheduleFocus }
    ),
    [quantity]
  );

  useEffect( removeFromBagHandler, [removeFromBagHandler] );

  /**
   * Handle add to bag response
   * -> We want to always run this any time quantity changes
   */
  const updateBagHandler = useCallback(
    utils.composeAddToBagResponse(
      { lastAction, animationFlag, quantity, descreaseButtonRef, increaseButtonRef, maxQuantity },
      { setLoader, setShowAnimation, setUpdateBagTriggered, scheduleFocus }
    ),
    [showUpdateBag]
  );

  useEffect( updateBagHandler, [updateBagHandler] );

  /**
   * ADA -> Broadcasts cart modifications for screen readers
   */
  const a11yHandler = useCallback(
    utils.composeA11yResponse(
      { quantity, inBagLabel, zeroLabel: '0 in bag', a11yAction },
      { broadcastMessage }
    ),
    [inBagLabel, quantity, broadcastMessage]
  );

  useEffect( a11yHandler, [a11yHandler] );

  /**
   * ADA focus should go to the increment button after the overlay close
   */
  useEffect( () => {
    // Track when overlay opens
    if( displayOverlay && !wasOverlayOpen.current ){
      wasOverlayOpen.current = true;
    }

    // When overlay closes and we should focus increment button
    if( !displayOverlay && wasOverlayOpen.current && shouldFocusIncrement.current ){
      scheduleFocus( () => {
        if( increaseButtonRef.current ){
          increaseButtonRef.current.focus();
        }
        // Clean up
        shouldFocusIncrement.current = false;
        wasOverlayOpen.current = false;
      } );
    }

    // Reset if overlay is closed
    if( !displayOverlay ){
      wasOverlayOpen.current = false;
    }
  }, [displayOverlay, scheduleFocus] );

  /**
   * Compose increase/decrease callbacks
   */
  const callbacksData = { props, lastAction, animationFlag, shouldFocusIncrement, a11yAction };
  const callbacksMethods = { invokeMutation, setLoader, setShowAnimation, setUpdateBagTriggered, setIsFulfillmentChanged, animationTimer };
  const { increaseQty, decreaseQty, initialIncrease, updateBag } = useMemo(
    () => utils.composeCallbacks( callbacksData, callbacksMethods ),
    [quantity, skuId, productId, commerceId, fulfillmentType, isSubscribed, subscriptionFrequency, addToBagAction]
  );

  const showUpdateBagButton = !loader && showUpdateBag && !!updateBagAction?.label;
  const showMainButton = !loader && !quantity && !showAnimation && !isSecondary;
  const showQtyButtons = !!quantity && !loader && !showUpdateBagButton;
  const showAddedMessage = !loader && !!quantity && !showAnimation && !isSecondary && !showUpdateBagButton;
  const showSecondaryButton = isSecondary && !loader && !quantity && !showAnimation;
  const addUpdateSuccessLabel = updateBagTriggered ? updateSuccessLabel : successLabel;
  const addToBagAccessibilityLabel = inBagLabel ? inBagLabel : addToBagAction?.label;
  const isMobile = inflection?.MOBILE;
  const derivedAriaLabel = isMobile && isWebStickyATBButton ? stickyAddToBagAccessibilityLabel : addToBagAccessibilityLabel;

  const atbAction = {
    ...addToBagAction,
    variables :{
      moduleParams: {
        skuId,
        productId,
        fulfillmentType,
        isSubscribed,
        subscriptionFrequency
      }
    }
  };

  const atbDataCaptureAction = {
    ...addToBagAction,
    // DC purpose only -- sending the `isSubscribed` & `subscriptionFrequency` as empty strings when undefined so DC event triggers
    // This is needed because they are configured in Amplience, but for MP skus the feature is not applicable and there is no way to separate DC configuration between MP and non-MP skus.
    dataCaptureData: utils.patchMethodDataCapture( { dataCaptureData: addToBagAction?.dataCaptureData, values: { isSubscribed : isSubscribed ?? '', subscriptionFrequency : subscriptionFrequency ?? '' } } )
  };

  return (
    <div className='AddToBagButton'
      role={ isMobile && isWebStickyATBButton && 'region' }
      aria-label={ derivedAriaLabel }
    >
      { showQtyButtons && (
        <Button
          iconSize={ 'm' }
          primary
          iconImage='Minus'
          className='AddToBagButton__Minus'
          id='decreaseQtyButton'
          withHover
          ariaLabel={ removeFromBagAction?.accessibility?.label }
          onClick={ () => {
            decreaseQty();
          } }
          action={ removeFromBagAction }
          ref={ descreaseButtonRef }
        />
      ) }

      { showAnimation && (
        <div className='AddToBagButton__AddedProgress'>
          <div className='AddToBagButton__SvgContainer'>
            <Text htmlTag='span'>
              <Icon className={ classNames( 'AddToBagButton__Check' ) }
                aria-hidden={ true }
                size={ 's' }
                name={ 'Check' }
                burst
              />
            </Text>
          </div>
          <Text htmlTag='span'
            textStyle='body-3'
          >
            { addUpdateSuccessLabel }
          </Text>
        </div>
      ) }

      { showMainButton && (
        <Button
          { ...{ onClick: initialIncrease, className: 'AddToBagButton__AddToBag' } }
          label={ inBagLabel ? inBagLabel : addToBagAction?.label }
          ariaLabel={ inBagLabel ? inBagLabel : addToBagAction?.label }
          action={ atbDataCaptureAction }
          ref={ mainButtonRef }
          disabled={ defaultState?.disabled }
        />
      ) }

      { showSecondaryButton && (
        <Button
          { ...{ onClick: initialIncrease, className: 'AddToBagButton__AddToBagOverlay' } }
          label={ inBagLabel ? inBagLabel : addToBagAction?.label }
          action={ atbAction }
          ref={ mainButtonRef }
          secondary
          disabled={ defaultState?.disabled }
          size='compact'
        />

      ) }

      { showAddedMessage && (
        <div className='AddToBagButton__Added'>
          <Text htmlTag='span'
            textStyle='body-2'
            fontWeight='bold'
          >
            { inBagLabel || addToBagAction?.label }
          </Text>
        </div>
      ) }

      { showQtyButtons && (
        <Button
          primary
          iconSize={ 'm' }
          className='AddToBagButton__Add'
          id='increaseQtyButton'
          { ...spreadProps( {
            disabled: quantity >= defaultState?.maxQuantity,
            secondary: quantity >= defaultState?.maxQuantity
          } ) }
          iconImage='Add'
          withHover
          ariaLabel={ incrementBagAccessibilityLabel }
          onClick={ () => {
            increaseQty();
          } }
          action={ addToBagAction }
          ref={ increaseButtonRef }
        />
      ) }
      { showUpdateBagButton && (
        <Button
          onClick={ updateBag }
          className='AddToBagButton__AddToBag AddToBagButton__UpdateBag'
          label={ updateBagAction?.label }
          action={ updateBagAction }
          ref={ mainButtonRef }
        />
      ) }
    </div>
  );
};

/**
 * Compose callback to handle add to bag button click
 * @param {object} data - data object passed in as an argument
 * @param {object} data.lastAction - React ref to store last user action
 * @param {object} data.props - props
 * @param {object} data.animationFlag - React ref to get response
 * @param {object} methods - object of methods passed in as an argument
 * @param {function} methods.invokeMutation - invoke mutation
 * @param {function} methods.setLoader - set state when add/remove is in progress
 * @param {function} methods.setShowAnimation- set state to show/hide animation
 * @param {function} methods.setUpdateBagTriggered- set state to required success label
 * @returns {{ decreaseQty, increaseQty, initialIncrease }} Handles quantity while click on add button
 */
export const composeCallbacks = ( data, methods ) => {
  const { props, lastAction, animationFlag, shouldFocusIncrement, a11yAction } = data || {};
  const { invokeMutation, setLoader, setShowAnimation, setUpdateBagTriggered, setIsFulfillmentChanged, animationTimer } = methods || {};

  const { quantity, skuId, productId, removeFromBagAction, addToBagAction, updateBagAction, fulfillmentType, isSubscribed, subscriptionFrequency } = props || {};

  /**
   * Remove from bag handler
   */
  const decreaseQty = () => {
    lastAction.current = BAG_ACTIONS.Remove;
    if( a11yAction ){
      a11yAction.current = true;
    }
    invokeMutation( {
      graphql: removeFromBagAction.graphql,
      customHeaders: removeFromBagAction.customHeaders,
      params: removeFromBagAction.params,
      variables: {
        contentId: props.id,
        moduleParams: { quantity: quantity - 1, fulfillmentType, isSubscribed, subscriptionFrequency }
      }
    } );
  };

  /**
   * Add to bag handler
   */
  const increaseQty = () => {
    lastAction.current = BAG_ACTIONS.Add;
    if( a11yAction ){
      a11yAction.current = true;
    }
    let newQty = quantity || 0;
    invokeMutation( {
      graphql: addToBagAction.graphql,
      customHeaders: addToBagAction.customHeaders,
      params: addToBagAction.params,
      variables: {
        contentId: props.id,
        moduleParams: { quantity: newQty + 1, productId, skuId, fulfillmentType, isSubscribed, subscriptionFrequency }
      }

    } );
  };

  /**
   * Update bag handler
   */
  const invokeUpdateBagMutation = () => {
    lastAction.current = BAG_ACTIONS.Add;
    if( a11yAction ){
      a11yAction.current = true;
    }
    let newQty = quantity || 0;
    invokeMutation( {
      graphql: updateBagAction.graphql,
      customHeaders: updateBagAction.customHeaders,
      params: updateBagAction.params,
      variables: {
        contentId: props.id,
        moduleParams: { quantity: newQty, productId, skuId, fulfillmentType, isSubscribed, subscriptionFrequency }
      }

    } );
  };

  /**
   * Hanadles initial add to bag
   */
  const initialIncrease = () => {
    setLoader( true );

    if( !quantity && shouldFocusIncrement ){
      shouldFocusIncrement.current = true;
    }

    increaseQty();

    if( setIsFulfillmentChanged ){
      setIsFulfillmentChanged( false );
    }
    animationFlag.current = false;
    setShowAnimation( true );
    animationTimer( () => {
      if( animationFlag.current ){
        animationFlag.current = false;
        setShowAnimation( false );
      }
      else {
        animationFlag.current = true;
      }
    }, ADD_TO_BAG_ANIMATION_DURATION );
  };

  /**
   * Hanadles initial update bag
   */
  const updateBag = () => {
    setLoader( true );
    setUpdateBagTriggered( true );
    if( setIsFulfillmentChanged ){
      setIsFulfillmentChanged( false );
    }
    invokeUpdateBagMutation();
    animationFlag.current = false;
    setShowAnimation( true );
    animationTimer( () => {
      if( animationFlag.current ){
        animationFlag.current = false;
        setShowAnimation( false );
      }
      else {
        animationFlag.current = true;
      }
    }, ADD_TO_BAG_ANIMATION_DURATION );
  };

  return { decreaseQty, increaseQty, initialIncrease, updateBag };
};

/**
 * @const {string} ADD_TO_BAG_ANIMATION_DURATION - Duration of add to bag animation
 */
export const ADD_TO_BAG_ANIMATION_DURATION = 1500;

/**
 * @const {object} BAG_ACTIONS - Tracks which user action was last performed
 */
export const BAG_ACTIONS = {
  Add: 'Add',
  Remove: 'Remove',
  Init: 'Init'
};

/**
 * Compose add to bag response
 * @param {object} data - data object passed in as an argument
 * @param {object} data.lastAction - React ref to store last user action
 * @param {object} data.animationFlag - React ref to get response
 * @param {object} methods - object of methods passed in as an argument
 * @param {function} methods.setLoader - set state when add/remove is in progress
 * @param {function} methods.setShowAnimation- set state to show/hide animation
 */
export const composeAddToBagResponse = ( data, methods ) => () => {
  const { lastAction, animationFlag, descreaseButtonRef, increaseButtonRef } = data || {};
  const { setLoader, setShowAnimation, setUpdateBagTriggered, scheduleFocus } = methods || {};

  if( lastAction?.current !== BAG_ACTIONS.Add ){
    return;
  }

  lastAction.current = BAG_ACTIONS.Init;

  setLoader( false );

  if( !animationFlag.current ){
    animationFlag.current = true;
  }
  else {
    animationFlag.current = false;
    setShowAnimation( false );
  }

  setUpdateBagTriggered( false );

  scheduleFocus( () => {
    if( increaseButtonRef.current?.disabled ){
      descreaseButtonRef.current?.focus();
    }
    else {
      increaseButtonRef.current?.focus();
    }
  } );
};

/**
 * Compose remove to bag response
 * @param {object} data - data object passed in as an argument
 * @param {object} data.lastAction - React ref to store last user action
 * @param {object} data.animationFlag - React ref to get response
 * @param {object} methods - object of methods passed in as an argument
 * @param {function} methods.setLoader - set state when add/remove is in progress
 * @param {function} methods.setShowAnimation- set state to show/hide animation
 */
export const composeRemoveFromBagResponse = ( data, methods ) => () => {
  const { lastAction, animationFlag, quantity, mainButtonRef } = data || {};
  const { setLoader, setShowAnimation, scheduleFocus } = methods || {};

  if( lastAction?.current !== BAG_ACTIONS.Remove ){
    return;
  }

  lastAction.current = BAG_ACTIONS.Init;
  setLoader( false );
  setShowAnimation( false );
  animationFlag.current = false;

  if( quantity > 0 ){
    return;
  }

  scheduleFocus( () => {
    mainButtonRef.current?.focus();
  } );
};

/**
 * Compose A11y response
 * @param {object} data - data object passed in as an argument
 * @param {number} data.quantity - bag count
 * @param {string} data.inBagLabel - Add button have qunatity label
 * @param {string} data.zeroLabel - Add button label
 */
export const composeA11yResponse = ( data, methods ) => () => {
  const { quantity, inBagLabel, zeroLabel, a11yAction } = data || {};
  const { broadcastMessage } = methods || {};
  if( !broadcastMessage ){
    return;
  }

  if( a11yAction && !a11yAction.current ){
    return;
  }

  if( a11yAction ){
    a11yAction.current = false;
  }

  if( quantity ){
    broadcastMessage( { message: inBagLabel, id: ADD_TO_BAG_ADA_EVENT, delay: ADD_TO_BAG_ADA_INTERVAL } );
  }
  else {
    broadcastMessage( { message: zeroLabel, id: ADD_TO_BAG_ADA_EVENT, delay: ADD_TO_BAG_ADA_INTERVAL } );
  }
};

/**
 * Patch the data capturew data with subscription component datacapture derived value
 *
 * @method
 * @param {object} data - function parameters passed from event handler
 */
export const patchMethodDataCapture = ( data ) => {
  const { dataCaptureData, values } = data || {};
  const patchedData = values ? patchDataLayer( { dataCaptureData, values } ) : dataCaptureData;
  return patchedData;
};

/**
 * @const {string} ADD_TO_BAG_ADA_EVENT - ADA broadcast event id
 */
export const ADD_TO_BAG_ADA_EVENT = 'addToBagBroadCast';

/**
 * @const {number} ADD_TO_BAG_ADA_INTERVAL - ADA broadcast cleanup timeout
 */
export const ADD_TO_BAG_ADA_INTERVAL = 30000;

/**
 * Property type definitions
 *
 * @typedef AddToBagButtonProps
 * @type {object}
 * @property {object} addToBagAction - Sets label and graphql
 * @property {object} updateBagAction - Sets label and graphql
 * @property {object} removeFromBagAction - Sets label and graphql
 * @property {string} successLabel - Sets success Label
 * @property {string} addToBagErrorLabel - Sets error Label
 * @property {string} inBagLabel - Sets in bag Label
 * @property {string} incrementBagAccessibilityLabel - Sets in bag Label
 * @property {number} maxQuantity - Sets max quantity
 * @property {number} quantity - quantity
 * @property {boolean} isSecondary - Sets type of the button
 * @property {string} fulfillmentType - fulfillmentType
 * @property {boolean} isSubscribed - Is Subscribed
 * @property {string} subscriptionFrequency - Subscription Frequency
 * @property {string} updateSuccessLabel - update success label
 * @property {function} setIsFulfillmentChanged - Function to reset the fulfillment change flag
 * @property {boolean} showUpdateBag - sets update bag label
 * @property {boolean} isWebStickyATBButton - shows sticky ATB btn
 * @property {string} stickyAddToBagAccessibilityLabel - sets ada label for sticky atb
 */
export const propTypes = {
  addToBagAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string,
    params: PropTypes.array,
    customHeaders: PropTypes.array
  } ),
  updateBagAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string
  } ),
  removeFromBagAction: PropTypes.shape( {
    label: PropTypes.string,
    graphql: PropTypes.string,
    params: PropTypes.array,
    customHeaders: PropTypes.array
  } ),
  successLabel: PropTypes.string,
  addToBagErrorLabel: PropTypes.string,
  inBagLabel: PropTypes.string,
  incrementBagAccessibilityLabel: PropTypes.string,
  maxQuantity: PropTypes.number,
  quantity: PropTypes.number,
  isSecondary: PropTypes.bool,
  fulfillmentType: PropTypes.string,
  isSubscribed: PropTypes.bool,
  subscriptionFrequency: PropTypes.string,
  updateSuccessLabel: PropTypes.string,
  setIsFulfillmentChanged: PropTypes.func,
  showUpdateBag: PropTypes.bool,
  isWebStickyATBButton: PropTypes.bool,
  stickyAddToBagAccessibilityLabel: PropTypes.string
};

AddToBagButton.propTypes = propTypes;

export default AddToBagButton;
